import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';

import { useNotification } from '../../../hooks/useNotification';
import { IndirectTaxTransactionHelper } from '../helpers';
import { TIndirectTaxJurisdictionBreakdown } from '../types';

interface IndirectTaxLineItemTaxBreakdownProps {
  rows: TIndirectTaxJurisdictionBreakdown[];
  onGenerateTaxBreakdown?: () => Promise<void>;
  isGenerating?: boolean;
  unavailableReason?: string | null;
}

const IndirectTaxLineItemTaxBreakdown = ({
  rows,
  onGenerateTaxBreakdown,
  isGenerating: isGeneratingProp,
  unavailableReason,
}: IndirectTaxLineItemTaxBreakdownProps) => {
  const { setNotification } = useNotification();
  const isGenerating = isGeneratingProp ?? false;
  const hasMarketplaceAmount = rows.some((row) => row.marketplace !== 0);

  const handleGenerate = async () => {
    if (!onGenerateTaxBreakdown) {
      return;
    }

    try {
      await onGenerateTaxBreakdown();
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Tax breakdown generation in progress',
      });
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to start tax breakdown generation',
      });
    }
  };

  if (rows.length === 0) {
    return (
      <div className="px-3xl pb-3xl flex flex-col gap-md">
        <p className="txt-small-regular text-text-tertiary">
          {isGenerating
            ? 'Generating tax breakdown. This may take a few seconds.'
            : (unavailableReason ??
              "A tax breakdown hasn't been generated for this line item yet.")}
        </p>
        {onGenerateTaxBreakdown && !unavailableReason && (
          <Button
            variant="primary"
            size="sm"
            isLoading={isGenerating}
            disabled={isGenerating}
            onClick={handleGenerate}
          >
            Generate tax breakdown
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="px-3xl pb-3xl">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Jurisdiction</TableHead>
            <TableHead className="text-right">Exempt</TableHead>
            <TableHead className="text-right">Non-taxable</TableHead>
            <TableHead className="text-right">Taxable</TableHead>
            {hasMarketplaceAmount && (
              <TableHead className="text-right">Marketplace</TableHead>
            )}
            <TableHead className="text-right">Tax / Fees</TableHead>
            <TableHead className="text-right">Rate</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.jurisdiction}-${row.jurisdictionType}`}>
              <TableCell>
                <div className="flex items-center gap-sm">
                  <span className="txt-small-regular text-text-primary">
                    {row.jurisdiction}
                  </span>
                  {row.jurisdictionType && (
                    <Badge variant="gray" size="sm">
                      {row.jurisdictionType}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right txt-small-regular text-text-primary">
                {IndirectTaxTransactionHelper.formatCurrency(row.exempt)}
              </TableCell>
              <TableCell className="text-right txt-small-regular text-text-primary">
                {IndirectTaxTransactionHelper.formatCurrency(row.nonTaxable)}
              </TableCell>
              <TableCell className="text-right txt-small-regular text-text-primary">
                {IndirectTaxTransactionHelper.formatCurrency(row.taxable)}
              </TableCell>
              {hasMarketplaceAmount && (
                <TableCell className="text-right txt-small-regular text-text-primary">
                  {IndirectTaxTransactionHelper.formatCurrency(row.marketplace)}
                </TableCell>
              )}
              <TableCell className="text-right txt-small-regular text-text-primary">
                {IndirectTaxTransactionHelper.formatCurrency(row.taxOrFees)}
              </TableCell>
              <TableCell className="text-right txt-small-regular text-text-primary">
                {row.rate}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMarketplaceAmount && (
        <p className="mt-md txt-small-regular text-text-tertiary">
          Marketplace amounts are sales on which the marketplace facilitator
          collects and remits the tax, so they are not split across the exempt,
          non-taxable and taxable buckets.
        </p>
      )}
    </div>
  );
};

export { IndirectTaxLineItemTaxBreakdown };
